import { Error as AuthError } from "@firebase/auth-types";
import { useState } from "react";
import { FieldValues, SubmitHandler } from "react-hook-form";

import { useIsMountedRef as useIsMountedReference } from "./useIsMountedRef";

export interface UseSubmitStateProperties<T extends FieldValues> {
  defaultErrorMessage?: string;
  onSubmit?: SubmitHandler<T>;
}

export interface UseSubmitStateValues<T extends FieldValues> {
  submitError: null | string;
  submitHandler: SubmitHandler<T>;
  submitting: boolean;
}

function isFirebaseAuthError(error: unknown): error is AuthError {
  return typeof error === "object" && error !== null && typeof Reflect.get(error, "code") === "string";
}

export const useSubmitState = <T extends FieldValues>({
  defaultErrorMessage = "Une erreur est survenue",
  onSubmit,
}: UseSubmitStateProperties<T> = {}): UseSubmitStateValues<T> => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<null | string>(null);
  const isMountedReference = useIsMountedReference();

  return {
    submitError,
    submitHandler: async (data, event) => {
      if (!submitting) {
        setSubmitError(null);
        setSubmitting(true);
        try {
          await onSubmit?.(data, event);
        } catch (error) {
          if (isMountedReference.current) {
            if (typeof error === "string") {
              setSubmitError(error);
            } else if (isFirebaseAuthError(error)) {
              setSubmitError(error.code);
            } else if (error instanceof Error) {
              setSubmitError(error.message);
            } else {
              setSubmitError(defaultErrorMessage);
            }
          }
        }
        if (isMountedReference.current) {
          setSubmitting(false);
        }
      }
    },
    submitting,
  };
};
